// ═══════════════════════════════════════════════════════════════════════════
// FINANCIAL DEFAULTS — Default waarden en ranges voor Business Case module
// ═══════════════════════════════════════════════════════════════════════════

export interface FinancialParams {
  // CAPEX
  pvCapex: number;          // €/kWp
  bessCapex: number;        // €/kWh
  backupCapex: number;      // €/kW
  chargerCapex: number;     // €/truck
  gridConnectionCapex: number; // € lump sum
  engineeringPct: number;   // % of total CAPEX

  // OPEX
  gridImportTariff: number; // €/kWh
  gridExportTariff: number; // €/kWh
  backupFuelCost: number;   // €/kWh_e
  omPvPct: number;          // % of PV CAPEX per year
  omBessPerKwh: number;     // €/kWh/year
  insurancePct: number;     // % of total CAPEX
  inflation: number;        // %/year

  // Revenue
  chargingTariff: number;   // €/kWh
  sdeSubsidy: number;       // €/kWh
  sdeMaxYears: number;      // max years for SDE++
  tariffEscalation: number; // %/year
  projectDuration: number;  // years
  wacc: number;             // %

  // Financieringsstructuur
  debtRatio: number;        // % van CAPEX gefinancierd met schuld
  interestRate: number;     // % rente op schuld
  loanTerm: number;         // jaren aflossing

  // BESS Vervanging
  bessReplacementYear: number;          // jaar van vervanging
  bessReplacementCostReduction: number; // % kostenreductie t.o.v. origineel

  // Dynamische energieprijzen
  energyPriceVolatility: number; // 0-100, mate van niet-lineaire prijsontwikkeling

  // CO2
  co2GridFactor: number;    // kg CO2/kWh NL gridmix
  co2CertPrice: number;     // €/ton CO2

  // Fiscaal
  vpbRate: number;           // % vennootschapsbelasting
  eiaPercentage: number;     // % Energie-investeringsaftrek
  miaPercentage: number;     // % Milieu-investeringsaftrek
  depreciationYears: number; // jaren lineaire afschrijving

  // ERE (Energie Reductie Eis)
  ereTariff: number;         // €/kWh geladen

  // Blokstroom
  blokstroomTariff: number;  // €/kWh apart tarief voor blokstroom-uren

  // Netbeheerdertarieven
  gridTransportTariff: number;   // €/kW/jaar transportkosten
  gridSystemServiceCost: number; // €/jaar systeemdienstenkosten
  gridMeteringCost: number;      // €/jaar meetkosten

  // Locatiehuur
  locationRentPerM2: number; // €/m²/jaar
  locationSizeM2: number;    // m²

  // Overige kosten (dynamisch)
  customOpexItems: CustomOpexItem[];

  // Laadinfra detail
  chargerDetailConfig?: ChargerConfig[];

  // Overige eenmalige kosten (dynamisch)
  customCapexItems: CustomCapexItem[];
}

export interface CustomOpexItem {
  id: string;
  name: string;
  annualCost: number;
}

export interface CustomCapexItem {
  id: string;
  name: string;
  amount: number;
}

export interface ChargerConfig {
  name: string;        // bijv. "DC Snellader 150kW"
  power: number;       // kW per paal
  count: number;       // aantal
  pricePerUnit: number; // € per paal
}

export interface ParamRange {
  min: number;
  max: number;
  step: number;
  unit: string;
  label: string;
}

export const FINANCIAL_DEFAULTS: FinancialParams = {
  pvCapex: 550,
  bessCapex: 300,
  backupCapex: 400,
  chargerCapex: 50000,
  gridConnectionCapex: 500000,
  engineeringPct: 10,

  gridImportTariff: 0.12,
  gridExportTariff: 0.06,
  backupFuelCost: 0.20,
  omPvPct: 1.0,
  omBessPerKwh: 5,
  insurancePct: 0.5,
  inflation: 2.5,

  chargingTariff: 0.30,
  sdeSubsidy: 0.05,
  sdeMaxYears: 15,
  tariffEscalation: 2.0,
  projectDuration: 20,
  wacc: 6,

  debtRatio: 70,
  interestRate: 4.5,
  loanTerm: 15,

  bessReplacementYear: 12,
  bessReplacementCostReduction: 30,

  energyPriceVolatility: 30,

  co2GridFactor: 0.4,
  co2CertPrice: 80,

  // Fiscaal
  vpbRate: 25.8,
  eiaPercentage: 40,
  miaPercentage: 27,
  depreciationYears: 15,

  // ERE
  ereTariff: 0.13,

  // Blokstroom
  blokstroomTariff: 0.08,

  // Netbeheerdertarieven (bron: Liander/Stedin 2025)
  gridTransportTariff: 45,     // €/kW/jaar — afhankelijk van netbeheerder en regio
  gridSystemServiceCost: 2500, // €/jaar — vast per aansluiting
  gridMeteringCost: 800,       // €/jaar — meetdiensten

  // Locatiehuur
  locationRentPerM2: 0,
  locationSizeM2: 0,

  // Overige kosten
  customOpexItems: [],
  customCapexItems: [],
};

export const FINANCIAL_RANGES: Partial<Record<keyof FinancialParams, ParamRange>> = {
  pvCapex:              { min: 0, max: 950, step: 10, unit: '€/kWp', label: 'PV CAPEX' },
  bessCapex:            { min: 0, max: 500, step: 10, unit: '€/kWh', label: 'BESS CAPEX' },
  backupCapex:          { min: 0, max: 600, step: 10, unit: '€/kW', label: 'Backup CAPEX' },
  chargerCapex:         { min: 0, max: 150000, step: 1000, unit: '€/truck', label: 'Laadinfra' },
  gridConnectionCapex:  { min: 0, max: 1500000, step: 10000, unit: '€', label: 'Netaansluiting' },
  engineeringPct:       { min: 0, max: 20, step: 1, unit: '%', label: 'Engineering & onvoorzien' },

  gridImportTariff:     { min: 0, max: 0.20, step: 0.01, unit: '€/kWh', label: 'Import tarief' },
  gridExportTariff:     { min: 0, max: 0.12, step: 0.01, unit: '€/kWh', label: 'Export tarief' },
  backupFuelCost:       { min: 0, max: 0.40, step: 0.01, unit: '€/kWh', label: 'Brandstofkosten' },
  omPvPct:              { min: 0, max: 2.0, step: 0.1, unit: '%', label: 'O&M PV' },
  omBessPerKwh:         { min: 0, max: 10, step: 0.5, unit: '€/kWh/j', label: 'O&M BESS' },
  insurancePct:         { min: 0, max: 1.0, step: 0.1, unit: '%', label: 'Verzekering' },
  inflation:            { min: 0, max: 5, step: 0.5, unit: '%/j', label: 'Inflatie' },

  chargingTariff:       { min: 0.20, max: 0.50, step: 0.01, unit: '€/kWh', label: 'Laadtarief' },
  sdeSubsidy:           { min: 0, max: 0.12, step: 0.01, unit: '€/kWh', label: 'SDE++ subsidie' },
  sdeMaxYears:          { min: 0, max: 15, step: 1, unit: 'jaar', label: 'SDE++ looptijd' },
  tariffEscalation:     { min: 0, max: 5, step: 0.5, unit: '%/j', label: 'Tariefescalatie' },
  projectDuration:      { min: 10, max: 25, step: 1, unit: 'jaar', label: 'Projectduur' },
  wacc:                 { min: 3, max: 12, step: 0.5, unit: '%', label: 'WACC' },

  debtRatio:            { min: 0, max: 100, step: 5, unit: '%', label: 'Schuldratio' },
  interestRate:         { min: 2, max: 8, step: 0.25, unit: '%', label: 'Rente' },
  loanTerm:             { min: 5, max: 20, step: 1, unit: 'jaar', label: 'Looptijd lening' },

  bessReplacementYear:          { min: 8, max: 25, step: 1, unit: 'jaar', label: 'BESS vervanging jaar' },
  bessReplacementCostReduction: { min: 0, max: 60, step: 5, unit: '%', label: 'BESS kostenreductie' },

  energyPriceVolatility: { min: 0, max: 100, step: 5, unit: '%', label: 'Prijsvolatiliteit' },

  co2GridFactor:        { min: 0.2, max: 0.6, step: 0.05, unit: 'kg/kWh', label: 'CO₂ gridmix' },
  co2CertPrice:         { min: 20, max: 150, step: 5, unit: '€/ton', label: 'CO₂ certificaatprijs' },

  // Fiscaal
  vpbRate:              { min: 15, max: 30, step: 0.1, unit: '%', label: 'VPB tarief' },
  eiaPercentage:        { min: 0, max: 45, step: 0.5, unit: '%', label: 'EIA aftrek' },
  miaPercentage:        { min: 0, max: 36, step: 1, unit: '%', label: 'MIA aftrek' },
  depreciationYears:    { min: 5, max: 20, step: 1, unit: 'jaar', label: 'Afschrijvingstermijn' },

  // ERE
  ereTariff:            { min: 0, max: 0.25, step: 0.01, unit: '€/kWh', label: 'ERE vergoeding' },

  // Blokstroom
  blokstroomTariff:     { min: 0, max: 0.20, step: 0.01, unit: '€/kWh', label: 'Blokstroom tarief' },

  // Netbeheerdertarieven
  gridTransportTariff:     { min: 0, max: 120, step: 1, unit: '€/kW/j', label: 'Transporttarief' },
  gridSystemServiceCost:   { min: 0, max: 10000, step: 100, unit: '€/j', label: 'Systeemdienstenkosten' },
  gridMeteringCost:        { min: 0, max: 3000, step: 50, unit: '€/j', label: 'Meetkosten' },

  // Locatiehuur
  locationRentPerM2:    { min: 0, max: 50, step: 0.5, unit: '€/m²/j', label: 'Huur per m²' },
  locationSizeM2:       { min: 0, max: 50000, step: 100, unit: 'm²', label: 'Oppervlakte locatie' },
};

// ── Scenario Presets ──────────────────────────────────────────────────────

export interface ScenarioPreset {
  name: string;
  icon: string;
  description: string;
  params: Partial<FinancialParams>;
}

export const SCENARIO_PRESETS: ScenarioPreset[] = [
  {
    name: 'Conservatief',
    icon: '🛡️',
    description: 'Voorzichtige aannames: hogere CAPEX, lagere opbrengsten, hogere WACC',
    params: {
      pvCapex: 650, bessCapex: 400, chargerCapex: 65000,
      chargingTariff: 0.25, sdeSubsidy: 0.03, wacc: 8,
      gridImportTariff: 0.14, tariffEscalation: 1.5,
    },
  },
  {
    name: 'Basis',
    icon: '📊',
    description: 'Standaard marktaannames — realistische projectie',
    params: { ...FINANCIAL_DEFAULTS },
  },
  {
    name: 'Optimistisch',
    icon: '🚀',
    description: 'Gunstige aannames: lagere CAPEX, hogere opbrengsten, lagere WACC',
    params: {
      pvCapex: 450, bessCapex: 220, chargerCapex: 40000,
      chargingTariff: 0.35, sdeSubsidy: 0.08, wacc: 5,
      gridImportTariff: 0.10, tariffEscalation: 3.0,
    },
  },
  {
    name: 'Zonder Subsidie',
    icon: '🚫',
    description: 'Geen SDE++ subsidie — puur commercieel',
    params: {
      sdeSubsidy: 0, sdeMaxYears: 0,
    },
  },
];
